<template>
  <v-dialog
    :value="dialog"
    :max-width="500"
    persistent
    :retain-focus="false"
    scrollable
    @keydown.esc="escape(closeDialog)"
    @click:outside="escape(closeDialog)"
    @keydown.enter="submit"
  >
    <v-card outlined class="p-todo-edit-dialog">
      <v-card-title>
        {{ dialogTitle }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          tabindex="4"
          @click="escape(closeDialog)"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>
      <v-card-text class="mt-2">
        <v-form ref="form" @submit.stop.prevent>
          <v-text-field
            v-model="editedTodo.title"
            :auto-grow="true"
            :rules="rules.title"
            :label="$t('todo.edit.title')"
            prepend-icon="mdi-text-short"
            hide-details="auto"
            class="mb-3"
            tabindex="1"
            clearable
            autofocus
          ></v-text-field>
          <v-textarea
            v-model="editedTodo.description"
            :auto-grow="true"
            :label="$t('todo.edit.description')"
            hide-details="auto"
            rows="1"
            class="ml-9 mb-3"
            tabindex="1"
            clearable
          ></v-textarea>
          <v-autocomplete
            v-model="editedTodo.users"
            :items="usersCanBeResponsible"
            :label="$t('todo.edit.responsible')"
            item-value="id"
            item-text="fullName"
            hide-details="auto"
            prepend-icon="mdi-account-multiple-outline"
            class="mb-3"
            tabindex="1"
            multiple
            small-chips
            deletable-chips
            hide-selected
            return-object
            clearable
          ></v-autocomplete>
          <v-autocomplete
            v-model="editedTodo.projectId"
            :items="allProjects"
            :rules="rules.projectId"
            :return-object="false"
            :label="$t('todo.edit.project')"
            prepend-icon="mdi-animation-outline"
            item-value="id"
            item-text="title"
            hide-details="auto"
            class="mb-3"
            tabindex="1"
            clearable
          ></v-autocomplete>
          <p-date-picker
            v-model="editedTodo.deadline"
            :label="$t('todo.edit.deadline')"
            prepend-icon="mdi-calendar-blank-outline"
            input-style="width:50%"
            tabindex="1"
            clearable
          ></p-date-picker>

          <div class="d-flex">
            <v-icon class="mdi-rotate-270 mr-3 d-flex flex-column justify-start pt-4">mdi-attachment</v-icon>
            <p-file-input
              v-model="editedTodo.attachments"
              :library-file-type="fileType.todoAttachment"
              :label="$t('todo.edit.attacments')"
              hide-details="auto"
              multiple
              clearable
            ></p-file-input>
          </div>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text tabindex="3" @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text :disabled="!isModelChanged()" tabindex="2" color="primary" @click="submit">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapActions, mapGetters } from 'vuex'
import { fileType } from '@/constants'

import PDatePicker from '@/components/PDatePicker'
import PBtnIcon from '@/components/PBtnIcon'
import PFileInput from '@/components/PFileInput'

import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PDatePicker,
    PBtnIcon,
    PFileInput
  },


  mixins: [
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    accessMixin
  ],


  props: {
    todo: Object,
    status: Number
  },


  data () {
    return {
      fileType,
      addUserInput: '',
      editedTodo: {},
      defaultTodo: {
        id: '',
        projectId: '',
        title: '',
        description: '',
        deadline: null,
        status: this.status ?? 0,
        users: [],
        attachments: []
      }
    }
  },


  computed: {
    ...mapGetters('user', ['usersShort']),
    ...mapGetters('project', ['allProjects', 'currentProjectId']),

    usersCanBeResponsible () {
      let usersCanBeResponsible = []
      if (this.currentUserRoleIsUser) {
        usersCanBeResponsible.push(this.currentUser)
      } else {
        usersCanBeResponsible = this.usersShort
      }
      return usersCanBeResponsible
    },

    dialogTitle () {
      return this.todo ? this.$t('todo.editTodo') : this.$t('todo.createTodo')
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [
          rule.required()
        ],
        projectId: [rule.required()]
      }
    }
  },


  methods: {
    ...mapActions('todo', ['createAttachments', 'createTodo', 'updateTodo']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.todo) {
        this.removeFromChanges(this.todo)
      }
      const todo = this.getObjectCopy(this.todo || this.defaultTodo)
      this.editedTodo = todo
      this.editedTodo.projectId = todo.projectId || this.currentProjectId
      this.initWatchedModel(this.editedTodo)
      this.resetValidation()
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        const todo = this.getObjectCopy(this.editedTodo)
        if (todo.users.length) {
          todo.userIds = [...todo.users.map(x => x.id)]
        }
        delete todo.users

        this.showSnackbarProcessing()

        if (todo.id) {
          if (this.editedTodo.attachments.length) {
            const newAttachments = [...this.editedTodo.attachments.filter(el => !el.id)]
            const createdAttachments = await this.createAttachments(newAttachments)
            const existingAttachments = this.editedTodo.attachments.filter(el => el.id)
            todo.attachments = [...existingAttachments.map(el => el.id), ...createdAttachments.map(el => el.id)]
          }
          await this.updateTodo(todo)
          this.showSnackbarSuccess(this.$t('todo.todoUpdated'))
        } else {
          if (this.editedTodo.attachments.length) {
            const attachments = [...this.editedTodo.attachments]
            const createdAttachments = await this.createAttachments(attachments)
            todo.attachments = createdAttachments.map(el => el.id)
          }
          await this.createTodo(todo)
          this.showSnackbarSuccess(this.$t('todo.todoCreated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
